import React, {useContext, useEffect, useState} from 'react';
import withAuth from "../auth";
import { API_URL } from '../constants';
import {AuthContext} from "./AuthContext";
import axios from "axios";
import {Link, useNavigate} from "react-router-dom";
import ReactApexChart from 'react-apexcharts';
import moment from "moment/moment";
import echo from "../echo";
import WhitelabelBadge from "./WhitelabelBadge";
import {
    formatPickupDropoffDisplay,
    formatPickupOriginDisplay,
} from '../utils/locationDisplay';

const Dashboard = () => {
    const [loading, setLoading] = useState(false);
    const [totals, setTotals] = useState({});
    const {token, setToken, admin, setAdmin, hasPermission} = useContext(AuthContext);
    const navigate = useNavigate();

    const [liveRequests, setLiveRequests] = useState([]);
    const [liveRequestsLoading, setLiveRequestsLoading] = useState(false);

    const [chartData, setChartData] = useState({ series: [], categories: [] });
    const [chartWeeklyTotals, setChartWeeklyTotals] = useState({});

    const chartOptions = {
        chart: {
            height: 249,
            type: 'bar',
            toolbar: {
                show: false,
            },
            fontFamily: 'Nunito, sans-serif',
        },
        colors: ["#036fe7", "#00ff0c", "#f93a5a"],
        plotOptions: {
            bar: {
                dataLabels: {
                    enabled: false
                },
                columnWidth: '40%',
                endingShape: 'rounded',
            }
        },
        dataLabels: {
            enabled: false
        },
        stroke: {
            show: true,
            width: 2,
            endingShape: 'rounded',
            colors: ['transparent'],
        },
        responsive: [{
            breakpoint: 576,
            options: {
                stroke: {
                    show: true,
                    width: 1,
                    endingShape: 'rounded',
                    colors: ['transparent'],
                },
            },
        }],
        xaxis: {
            categories: [],
        },
        fill: {
            opacity: 1
        },
        legend: {
            show: false,
            floating: true,
            position: 'top',
            horizontalAlign: 'left',
        },
        tooltip: {
            y: {
                formatter: function (val) {
                    return val;
                }
            }
        },
        yaxis: {
            labels: {
                formatter: function (val) {
                    return Math.round(val); // Round to nearest whole number
                }
            },
        },
    };

    useEffect(() => {
        if (!admin?.id) return;

        const channel = echo.channel('np-admin-pickups');

        channel.listen('.App\\Events\\Admin\\PickupUpdated', (event) => {
            if (event.message_type === 'pickup_updated' && event.pickup) {
                if (event.pickup.delivery_status?.id !== 5 && event.pickup.delivery_status?.id !== 6 && event.pickup.waiting_for_driver_at) {
                    console.log('event.pickup', event.pickup);
                    setLiveRequests((prev) => [event.pickup, ...prev]);
                }
            }
        });

        return () => {
            echo.leave('np-admin-pickups');
        };
    }, [admin]);

    useEffect(() => {
        if (!liveRequests.length) return;

        const channels = liveRequests.map((p) => p.channel_id).filter(Boolean);

        channels.forEach((channelId) => {
            const c = echo.channel(channelId);

            c.listen('.App\\Events\\Admin\\PickupUpdated', (event) => {
                const updatedPickup = event.pickup;
                if (!updatedPickup) return;

                setLiveRequests((prevPickups) =>
                    prevPickups.map((p) =>
                        p.id === updatedPickup.id
                            ? { ...p, ...updatedPickup }
                            : p
                    )
                );
            });
        });

        return () => {
            channels.forEach((channelId) => {
                echo.leave(channelId);
            });
        };
    }, [liveRequests]);

    useEffect(() => {
        setLoading(true);
        axios.get(`${API_URL}/api/admin/dashboard/totals`, {
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        })
            .then(response => {
                setTotals(response.data);

                // Live Requests
                setLiveRequestsLoading(true);
                axios.post(`${API_URL}/api/admin/pickups/live-requests`, {page: 1}, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
                    .then(res => {
                        const items = Array.isArray(res.data?.data) ? res.data.data : [];

                        setLiveRequests(items);
                    })
                    .catch(error => {
                        console.error('Error fetching live requests', error);
                        if (error.response && error.response.status === 401) {
                            localStorage.removeItem('authToken');
                            setToken(null);
                            navigate('/');
                        }
                    })
                    .finally(() => setLiveRequestsLoading(false));

                // Week Pickup Stats
                setLoading(true);
                axios.get(`${API_URL}/api/admin/dashboard/pickup-stats`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                })
                    .then(response => {
                        setChartWeeklyTotals(response.data?.weekly_totals);

                        const dailyStats = response.data?.daily_stats;
                        const categories = dailyStats.map(item => item.date);
                        const created = dailyStats.map(item => item.total_pickups);
                        const delivered_pickups = dailyStats.map(item => item.delivered_pickups);
                        const canceled = dailyStats.map(item => item.canceled_pickups);

                        setChartData({
                            series: [
                                { name: 'Total Deliveries', data: created },
                                { name: 'Delivered Deliveries', data: delivered_pickups },
                                { name: 'Canceled Deliveries', data: canceled }
                            ],
                            categories: categories
                        });
                    })
                    .catch(error => {
                        console.error('There was an error fetching the admin details!', error);
                        if (error.response && error.response.status === 401) {
                            localStorage.removeItem('authToken');
                            setToken(null);
                            navigate('/');
                        }
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            })
            .catch(error => {
                console.error('There was an error fetching the admin details!', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }, [navigate, setToken, token]);

    const statusColors = {
        '0': 'transparent',
        '1': '#FFFAE5',  // Very Light Gold
        '2': '#FFCBB2',  // Very Light OrangeRed
        '3': '#CFF3C8',  // Very Light Green
        '4': '#C7D6E9',  // Very Light SteelBlue
        '5': '#D1F2CF',  // Very Light LimeGreen
        '6': '#FFA3B5'   // Very Light Crimson
    };

    const truncateWithEllipsis = (text, maxLength = 30) => {
        if (text.length > maxLength) {
            return (
                <span title={text}>
                    {text.substring(0, maxLength)}...
                </span>
            );
        }
        return text;
    };

    return (
        <div className="container mt-3">
            {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
                    <div className="spinner-border" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            ) : (
                <>
                    {hasPermission("dashboard-stat-view") && (
                        <div className="row d-sm-flex">
                        <div className="col-xl-3 col-lg-6 col-md-6 col-xm-12">
                            <div className="card overflow-hidden sales-card bg-primary-gradient">
                                <div className="pl-3 pt-2 pr-3 pb-2 pt-0">
                                    <div className="pb-0 mt-0">
                                        <Link to="/users">
                                            <div className="row">
                                                <div className="col-6">
                                                    <h4 className="tx-13 font-weight-bold mb-1 text-white">Customers:</h4>
                                                </div>
                                                <div className="col-6 text-right">
                                                    <h4 className="tx-18 font-weight-bold mb-1 text-white">
                                                        {totals?.customers?.all || 0}<br/>
                                                        today:&nbsp;+{totals?.customers?.today || 0}
                                                    </h4>
                                                </div>
                                            </div>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-xl-3 col-lg-6 col-md-6 col-xm-12">
                            <div className="card overflow-hidden sales-card bg-danger-gradient">
                                <div className="pl-3 pt-2 pr-3 pb-2 pt-0">
                                    <div className="pb-0 mt-0">
                                        <Link to="/drivers">
                                            <div className="row">
                                                <div className="col-6">
                                                    <h4 className="tx-13 font-weight-bold mb-1 text-white">Drivers:</h4>
                                                </div>
                                                <div className="col-6 text-right">
                                                    <h4 className="tx-18 font-weight-bold mb-1 text-white">
                                                        {totals?.drivers?.all || 0}<br/>
                                                        today:&nbsp;+{totals?.drivers?.today || 0}
                                                    </h4>
                                                </div>
                                            </div>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-xl-3 col-lg-6 col-md-6 col-xm-12 ">
                            <div className="card overflow-hidden sales-card bg-success-gradient">
                                <div className="pl-3 pt-2 pr-3 pb-2 pt-0">
                                    <div className="pb-0 mt-0">
                                        <Link to="/pickups">
                                            <div className="row">
                                                <div className="col-5">
                                                    <h4 className="tx-13 font-weight-bold mb-1 text-white">Deliveries:</h4>
                                                </div>
                                                <div className="col-7 text-right">
                                                    <h4 className="tx-18 font-weight-bold mb-1 text-white">
                                                        {totals?.pickups?.all || 0}<br />
                                                        today:&nbsp;+{totals?.pickups?.today || 0}
                                                    </h4>
                                                </div>
                                            </div>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-xl-3 col-lg-6 col-md-6 col-xm-12">
                            <div className="card overflow-hidden sales-card bg-warning-gradient">
                                <div className="pl-3 pt-2 pr-3 pb-2 pt-0">
                                    <div className="pb-0 mt-0">
                                        <Link to="/">
                                            <div className="row">
                                                <div className="col-6">
                                                    <h4 className="tx-13 font-weight-bold mb-1 text-white">Weekly driver earnings:</h4>
                                                </div>
                                                <div className="col-6 text-right">
                                                    <h4 className="tx-18 font-weight-bold mb-1 text-white">${totals?.driver_earnings || 0.00}<br /><br /></h4>
                                                </div>
                                            </div>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    )}

                    <div>
                        <p className="text-center">Welcome {admin?.name}!</p>
                        {!admin?.slack_user_id && (
                            <h5 className='heading-info text-center pt-2 pb-2'>
                                Slack 2FA <br />
                                <small>
                                    Please setup Slack for more convenient 2FA access <br />
                                    <a href={`${API_URL}/admin/login/slack`}>Setup Slack 2FA</a>
                                </small>
                            </h5>
                        )}
                    </div>

                    {hasPermission("dashboard-stat-view") && (
                        <div className="row row-sm">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header bg-transparent pd-b-0 pd-t-20 bd-b-0">
                                    <div className="d-flex justify-content-between">
                                        <h4 className="card-title mb-0">Deliveries for the Week</h4>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <div className="total-revenue">
                                        <div>
                                            <h4>{chartWeeklyTotals.created_pickups}</h4>
                                            <label><span className="bg-primary"/>Total</label>
                                        </div>
                                        <div>
                                            <h4>{chartWeeklyTotals.paid_pickups}</h4>
                                            <label><span className="bg-success"/>Delivered</label>
                                        </div>
                                        <div>
                                            <h4>{chartWeeklyTotals.canceled_pickups}</h4>
                                            <label><span className="bg-danger"/>Canceled</label>
                                        </div>
                                    </div>
                                    <div id="bar" className="sales-bar mt-4">
                                        <ReactApexChart
                                            options={{ ...chartOptions, xaxis: { categories: chartData.categories } }}
                                            series={chartData.series}
                                            type="bar"
                                            height={249}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    )}

                    <div className="row row-sm mt-3">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header bg-transparent pd-b-0 pd-t-20 bd-b-0">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex justify-content-between align-items-baseline">
                                            <h4 className="card-title mb-0">Live Requests</h4>
                                            <Link to="/pickups/live-requests" className="mx-2">
                                                View all
                                            </Link>
                                        </div>
                                    </div>
                                </div>

                                <div className="card-body">
                                    {liveRequestsLoading ? (
                                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 120 }}>
                                            <div className="spinner-border" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                        </div>
                                    ) : liveRequests.length === 0 ? (
                                        <div className="text-center text-muted">
                                            No live requests right now.
                                        </div>
                                    ) : (
                                        <>
                                            <div className="table-responsive">
                                                <table className="table table-sm table-striped text-center text-nowrap">
                                                    <thead>
                                                    <tr>
                                                        <th>ID</th>
                                                        <th>Whitelabel</th>
                                                        <th>Status</th>
                                                        <th>Customer</th>
                                                        <th>Driver</th>
                                                        <th>Truck Type</th>
                                                        <th>Cost / Dist.</th>
                                                        <th>Locations</th>
                                                        <th>Booked At</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody className="text-wrap">
                                                    {liveRequests.map((pickup) => (
                                                        <tr key={pickup.id}>
                                                            <td style={{backgroundColor: !pickup.delivery_status?.id ? 'initial' : statusColors[pickup.delivery_status?.id] || 'initial'}}>
                                                                <Link to={`/pickups/${pickup.id}`}>
                                                                    {pickup.id}
                                                                </Link>
                                                            </td>
                                                            <td><WhitelabelBadge whitelabelId={pickup.whitelabel_id}/></td>
                                                            <td>
                                                                {pickup.waiting_for_driver_at &&
                                                                    <div className="badge badge-light">{pickup.delivery_status?.status}</div>}
                                                            </td>
                                                            <td>
                                                                {pickup.user &&
                                                                    <Link to={`/users/${pickup.user.id}`}>
                                                                        {pickup.user.id}<br/>{pickup.user.full_name}
                                                                    </Link>
                                                                }
                                                            </td>
                                                            <td>
                                                                {pickup.driver &&
                                                                    <Link to={`/drivers/${pickup.driver.id}`}>
                                                                        {pickup.driver.id}<br/>{pickup.driver.name}
                                                                    </Link>
                                                                }
                                                            </td>
                                                            <td>{pickup.truck_type?.title}</td>
                                                            <td>${pickup.cost}<br/>{pickup.distance} km</td>
                                                            <td className="text-nowrap">
                                                                {truncateWithEllipsis(formatPickupOriginDisplay(pickup))}<br/>
                                                                {truncateWithEllipsis(formatPickupDropoffDisplay(pickup))}
                                                            </td>
                                                            <td>
                                                                {moment(pickup.waiting_for_driver_at).format('YYYY-MM-DD HH:mm')}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {(liveRequests.length > 10) && (
                                                <div className="text-center mt-3">
                                                    <Link to="/pickups/live-requests">
                                                        There are more than {liveRequests.length}. View all live requests
                                                    </Link>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default withAuth(Dashboard);
